import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { nanoid } from "nanoid";
import ReusableBreadcrumbs from "../../CommonComponents/ReusableBreadCrumbs";
import Stepper from "../../CommonComponents/Stepper";
import TestSvgIcon from "../../../Svg/TestSvgIcon";
import TestForm from "./TestForm";
import TestConfig from "./TestConfig";
import StudentView from "./StudentView";
import { libTypeIds, MODE, RESULT_STATUS } from "../../../constants";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import useAuth from "../../../Hooks/useAuth";
import useS3FileManagement from "../../../Hooks/useS3FileManagement";
import Loading from "../../Notification-Loading/Loading/Loading";
import Error404 from "../../../Error404";
import Toast from "../../CommonComponents/Toast";
import { useDebouncedFn } from "../../../Hooks/useDebounceFn";
import { getDataFromStorage } from "../../../util";
import AlertContext from "../../../context/AlertContext";
const initialValue = {
  name: "",
  description: "",
  duration: "",
  durationUnit: "m",
  instruction: "",
  mode: MODE.TEST_MODE,
  listView: 0,
  autoEvaluate: 0,
  videoAssessment: 0,
  enableNoteBook: 0,
  aiMode: 0,
  tags: [],
  sections: [
    {
      secId: nanoid(),
      secName: "Section - 1",
      isActive: true,
      status: "new",
      topics: [
        {
          id: nanoid(),
          topicId: null,
          topicName: null,
          configLibType: null,
          languages: [],
          complexityId: null,
          complexityName: null,
          availableQnCount: "",
          randomQnCount: null,
          fixedQnCount: null,
          fixedQnIds: [],
          marks: null,
          duration: null,
          durationUnit: "m",
          status: "new",
          subject: null,
          topics: []
        },
      ],
    },
  ],
  viewTest: 0,
  image: null,
};

const validators = {
  0: {
    name: (v) =>
      !v?.trim()
        ? "Name is required"
        : v?.length >= 100
          ? "Max characters: 100"
          : "",
    description: (v) => (v?.length >= 300 ? "Max characters: 300" : ""),
    duration: (v) =>
      !v || !Number.isInteger(+v) || +v <= 0 ? "Valid duration required" : "",
    durationUnit: (v) => (!v ? "Required" : ""),
    mode: (v) => (!v ? "Required" : ""),
    scope: (v, details, libTypeId) => {
      // For descriptive tests, allow scope OR aiMode OR videoAssessment
      if (parseInt(libTypeId) === libTypeIds.DESCRIPTIVE) {
        return !v && !details?.aiMode && !details?.videoAssessment
          ? "Please select any one of Company Specific, Technology Specific, or any Assessment Mode"
          : "";
      }
      // For other tests, require scope
      return !v
        ? "Please select any one Specific Company or Technology"
        : "";
    },

  },

  1: {
    secName: (v) =>
      !v?.trim()
        ? "Name is required"
        : v?.length >= 100
          ? "Max characters: 100"
          : "",
    topics: {
      topicId: (v) => (!v || !+v ? "Select the topic" : ""),
      complexityId: (v) => (!v || !+v ? "Select the difficulty" : ""),
      configLibType: (v) => (!+v ? "Select the type" : ""),
      languages: (v) => (!v || !v?.length ? "Select the languages" : ""),
      randomQnCount: (v) => (+v < 0 ? "Invalid number" : ""),
      fixedQnCount: (v, fIdV) =>
        +v < 0
          ? "Invalid number"
          : +v > 0 && !fIdV.length
            ? "Please configure questions"
            : +v > 0 && +v < fIdV.length
              ? "Please configure less questions"
              : +v > 0 && +v > fIdV.length
                ? "Please configure more questions"
                : "",
      marks: (v) => (!v || !+v ? "Invalid mark" : ""),
      duration: (v) => (!v || !+v || +v <= 0 ? "Invalid duration" : ""),
      durationUnit: (v) => (!v ? "Select the duration unit" : ""),
    },
  },

  2: {
    viewTest: (v) =>
      v === null || v === undefined ? "Select student view" : "",
  },
};

const CreateTest = ({ mockuse = 0 }) => {
  const Alert = useContext(AlertContext);
  const { type, testid } = useParams();
  const { isLoaded, setIsLoaded, navigate, setShowNotify } = useAuth();
  const axios = useAxiosPrivate();
  const [searchParms] = useSearchParams();

  const selectable = searchParms.get("selectable") === "true";
  const lpId = searchParms.get("lpId") || null;
  const moduleId = searchParms.get("moduleId") || null;
  const chapterId = searchParms.get("chapterId") || null;
  const batchId = searchParms.get("bId") || null;
  const isVideoInterview = searchParms.get("isVideoInterview") || null;
  const isAiInterview = searchParms.get("isAiInterview") || null;
  const isVideoQnsBool = isVideoInterview === "true";
  const isAiInterivewBool = isAiInterview === "true";
  const companyId = getDataFromStorage("companyId");
  console.log("Company Id for image key :", companyId);

  let queryString = `${selectable
    ? `?selectable=${selectable}${lpId ? `&lpId=${lpId}` : ""}${moduleId ? `&moduleId=${moduleId}` : ""
    }${chapterId ? `&chapterId=${chapterId}` : ""}${batchId ? `&bId=${batchId}` : ""
    }${isVideoQnsBool ? "&isVideoInterview=true" : ""}${isAiInterivewBool ? "&isAiInterivewBool=true" : ""}`
    : `${isVideoQnsBool ? "?isVideoInterview=true" : ""}${isAiInterivewBool ? "?isAiInterview=true" : ""}`
    }`;

  const baseRoute = batchId ? `/batches/${batchId}/courses` : "/learningpath";

  const isPublished =
    searchParms.get("published") === 1 || searchParms.get("published") === "1";

  const isEdit = Number.isInteger(+testid);

  const [activeStep, setActiveStep] = useState(0);
  const [testDetails, setTestDetails] = useState(initialValue);
  const [errors, setErrors] = useState({
    0: {},
    1: {
      sections: "",
      topics: {},
      section: {},
    },
    2: {},
  });

  const [topicErrors, setTopicErrors] = useState({});
  const [libTypeId, setLibTypeId] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);

  const { onUploadS3Object, getPreSignedUrl } = useS3FileManagement();

  // const qnCountCache = useMemo(() => new Map(), []);

  // const getQnCacheKey = (top) => `${top.topicId}_${top.complexityId}`;

  const getTags = async () => {
    try {
      const { data } = await axios.get("node/admin/tag/get", {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (data) {
        return data;
      } else {
        throw new Error("Unable to get tags. Please try again later");
      }
    } catch (error) {
      console.error("error in get tags", error);
      throw error;
    }
  };

  const getAvailableQnCount = async (topic, libTypeId) => {
    try {
      // const key = getQnCacheKey(topic);

      // if (qnCountCache.has(key)) {
      //   return qnCountCache.get(key);
      // }
      const { data } = await axios.post(
        "node/admin/question/getbytopicandcomplexity",
        {
          topicid: topic.topicId,
          languages: topic.languages ?? [],
          complexityid: topic.complexityId,
          libtypeid: libTypeId,
        },
      );
      if (data.resultCode === RESULT_STATUS.SUCCESS) {
        // qnCountCache.set(key, data.data.length ?? 0);

        return data.data.length ?? 0;
      } else {
        setShowNotify({
          title: "Error !",
          msg: "Unable to get available question count. Please try again later",
          show: true,
        });
      }
    } catch (error) {
      console.error("error in get available question count", error);

      let isUnAuth = error.message.includes("403");
      setShowNotify({
        isUnAuth,
        title: isUnAuth ? "Warning !" : "Error !",
        msg: isUnAuth
          ? "You have been logged-out due to inactivity. Login again."
          : "Unable to get available question count. Please try again later",
        show: true,
      });
      return null;
    }
  };

  const fetchAvailableCount = useCallback(
    async (topicId, topicData, libtypeId) => {
      try {
        setIsLoaded(true);
        const count = await getAvailableQnCount(topicData, libtypeId);
        console.log("topicId", topicId);
        console.log("count", count);

        return count;
      } catch (error) {
        let isUnAuth = error.message.includes("403");
        setShowNotify({
          isUnAuth,
          title: isUnAuth ? "Warning !" : "Error !",
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : "Unable to get test details. Please try again later",
          show: true,
        });
        return;
      } finally {
        setIsLoaded(false);
      }
    },
    [],
  );

  const debouncedFetchAvailableCount = useDebouncedFn(
    async (topicId, topicData, libtypeId) => {
      const count = await fetchAvailableCount(topicId, topicData, libtypeId);
      setTestDetails((prev) => {
        const copy = structuredClone(prev);
        const section = copy.sections.find((sec) => sec.isActive);
        const topic = section?.topics.find((t) => t.id === topicId);

        if (topic) {
          topic.availableQnCount = count;
        }

        return copy;
      });
    },
    1000,
  );

  useEffect(() => {
    if (isVideoQnsBool) {
      setTestDetails((prev) => ({
        ...prev,
        videoAssessment: 1,
      }));
    }
    if (parseInt(type) === libTypeIds.SQL) {
      setTestDetails((prev) => {
        let copy = structuredClone(prev);
        copy.sections[0].secName = "SQL Section";
        return copy;
      });
    }

    if (parseInt(type) === libTypeIds.CODING_SNIPPET) {
      setTestDetails((prev) => {
        let copy = structuredClone(prev);
        copy.autoEvaluate = 1;
        return copy;
      });
    }

    if (parseInt(type) === libTypeIds.CODING_CHALLENGE) {
      delete validators[0].duration;
      delete validators[0].durationUnit;
      setTestDetails((prev) => {
        let copy = structuredClone(prev);
        copy.sections[0].secName = "Coding Section";
        return copy;
      });
    } else {
      validators[0].duration = (v) =>
        !v || !Number.isInteger(+v) || +v <= 0 ? "Valid duration required" : "";
      validators[0].durationUnit = (v) => (!v ? "Required" : "");
    }
    if (mockuse === 1)
      validators[0].libTypeId = (v) =>
        !Number.isInteger(+v) ? "Please select test type" : "";
    else delete validators[0].libTypeId;
    setLibTypeId(type ?? libTypeIds.MCQ);
  }, [type, mockuse]);

  console.log("test", testDetails);

  /* -------- For update -------- */
  useEffect(() => {
    const getTest = async () => {
      try {
        setIsLoaded(true);
        const { data } = await axios.get(`node/admin/test/getbyid/${testid}`);
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          const details = structuredClone(
            JSON.parse(data.data ?? "[]")[0] ?? {},
          );
          if (mockuse === 1) {
            let tags = await getTags();
            tags = tags.map((tag) => ({ value: tag.id, label: tag.name }));
            details.tags = details.tags
              ?.split(",")
              .map((tag) => {
                const matched = tags.find((t) => t.value === +tag);
                return matched ? matched.value : null;
              })
              .filter(Boolean);
            setTags(tags ?? []);
          }
          if (!Object.keys(details).length) return;
          if (details.sections[0]) details.sections[0].isActive = true;
          for (const sec of details.sections) {
            sec.status = "old";
            for (const top of sec.topics) {
              if (
                parseInt(top.configLibType ?? type ?? libTypeId) ===
                libTypeIds.CODING_CHALLENGE
              )
                top.languages = top.languages?.map((lang) => lang.id) ?? [];
              top.id = top.id ?? nanoid();
              top.status = "old";
              const questionCount =
                (top.fixedQnCount || 0) + (top.randomQnCount || 0);
              top.duration = top.duration / questionCount;
              top.availableQnCount = await getAvailableQnCount(
                top,
                top.configLibType ?? type ?? libTypeId,
              );
              top.fixedQnIds = (top.fixedQnIds?.split(",") ?? []).map((qnid) =>
                parseInt(qnid),
              );
            }
          }
          setLibTypeId(details.libTypeId);
          setTestDetails(details);
        } else {
          setShowNotify({
            show: true,
            msg:
              data.data?.msg ??
              "Unable to get test details. Please try again later",
            title: "Error !",
          });
          navigate(
            mockuse === 1
              ? `/library/mock/test`
              : `/library/${type}/testlist${queryString}`,
          );
        }
      } catch (error) {
        console.error("error in get test details", error);

        let isUnAuth = error.message.includes("403");
        setShowNotify({
          isUnAuth,
          title: isUnAuth ? "Warning !" : "Error !",
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : "Unable to get test details. Please try again later",
          show: true,
        });
      } finally {
        setIsLoaded(false);
      }
    };
    if (Number.isInteger(Number(testid))) getTest();
    else {
      getTags()
        .then((tags) => {
          setTags(
            tags.map((tag) => ({ value: tag.id, label: tag.name })) || [],
          );
        })
        .catch((error) => {
          let isUnAuth = error.message.includes("403");
          setShowNotify({
            isUnAuth,
            title: isUnAuth ? "Warning !" : "Error !",
            msg: isUnAuth
              ? "You have been logged-out due to inactivity. Login again."
              : "Unable to get tags. Please try again later",
            show: true,
          });
        });
    }
  }, [testid]);

  const currentSection = useMemo(
    () => testDetails.sections.find((section) => section.isActive),
    [testDetails],
  );

  /* -------- FIELD CHANGER HANDLER ------- */

  const onChangeDetailFields = (field, value, extraUpdates = {}) => {
    if (field === "libTypeId") {
      setLibTypeId(value);
      if (mockuse === 1 && value === libTypeIds.CODING_CHALLENGE) {
        delete validators[0].duration;
        delete validators[0].durationUnit;
      } else {
        validators[0].duration = (v) =>
          !v || !Number.isInteger(+v) || +v <= 0
            ? "Valid duration required"
            : "";
        validators[0].durationUnit = (v) => (!v ? "Required" : "");
      }
    }
    setTestDetails((prev) => {
      const copy = structuredClone(prev);

      if (field === "videoAssessment" && value === 1) {
        if (copy.sections?.length) {
          for (const sec of copy.sections) {
            for (const top of sec.topics) {
              delete top.configLibType;
            }
          }
        }
      }

      copy[field] = value;
      Object.entries(extraUpdates).forEach(([key, val]) => {
        copy[key] = val;
      });
      return copy;
    });


    if (field === "mode" && +value === MODE.PRACTICE_MODE) {
      steps.filter((step) => step !== 3);
    }

    // Update testDetails first for validation context
    const updatedDetails = structuredClone(testDetails);
    updatedDetails[field] = value;
    Object.entries(extraUpdates).forEach(([key, val]) => {
      updatedDetails[key] = val;
    });
    const selectedLibTypeId = field === "libTypeId" ? value : libTypeId;
    validateField(0, field, value, updatedDetails, selectedLibTypeId);

    // If aiMode or videoAssessment changes, re-validate scope since they're interdependent
    // This will clear the error if user selects an assessment mode
    if (
      field === "aiMode" ||
      field === "videoAssessment" ||
      extraUpdates.aiMode !== undefined ||
      extraUpdates.videoAssessment !== undefined
    ) {
      setErrors((prev) => {
        if (!prev[0]?.scope) return prev;

        return {
          ...prev,
          0: {
            ...prev[0],
            scope: validators[0].scope(
              updatedDetails.scope,
              updatedDetails,
              selectedLibTypeId,
            ),
          },
        };
      });
    }
  };

  const onChangeSectionName = (e) => {
    let value = e.target.value;
    const validator = validators[1]?.secName;
    if (!validator) return;

    let secErr = validator(value);
    setErrors((prev) => {
      let copy = structuredClone(prev);
      if (!copy[1]) copy[1] = {};
      if (!copy[1].section) copy[1].section = {};
      if (!copy[1].section[currentSection.secId])
        copy[1].section[currentSection.secId] = {};
      if (!secErr) delete copy[1].section[currentSection.secId];
      else copy[1].section[currentSection.secId].secName = secErr;
      return copy;
    });
    setTestDetails((prev) => {
      const details = structuredClone(prev);
      const section = details.sections.find((sec) => sec.isActive);
      if (section) {
        section.secName = value;
      }
      return details;
    });
  };

  const onChangeTopicField = async (id, field, value) => {
    const details = structuredClone(testDetails);

    const section = details.sections.find((sec) => sec.isActive);
    if (!section) return;

    const topic = section.topics.find((top) => top.id === id);
    if (!topic) return;

    if (+type === libTypeIds.MCQ && field === "subject") {
      setTestDetails((prev) => ({
        ...prev,
        sections: prev.sections.map((sec, secIndex) => {
          return {
            ...sec,
            topics: sec.topics.map((top) =>
              top.id === topic.id
                ? { ...top, topicId: null }
                : top
            )
          };
        })
      }));
    }
    // 1️⃣ Apply change FIRST
    topic[field] = value;

    // ----------------------------------
    // 2️⃣ Duplicate configuration check
    // ----------------------------------
    const map = {};

    for (const sec of details.sections) {
      for (const top of sec.topics) {
        const { topicId, complexityId, configLibType } = top;

        if (!topicId || !complexityId) continue;

        const key = `${topicId}_${complexityId}${parseInt(libTypeId) === libTypeIds.DESCRIPTIVE ||
          parseInt(libTypeId) === libTypeIds.CODING_SNIPPET
          ? `_${configLibType}`
          : ""
          }`;

        if (map[key]) {
          setToastMessage("This topic and difficulty is already configured.");
          setToastOpen(true);
          return;
        }
        map[key] = true;
      }
    }

    // -------------------------------
    // 4️⃣ Available question count
    // -------------------------------
    if (
      allowToCheckAvailableQn(
        libTypeId,
        topic,
        field,
        testDetails.videoAssessment,
      )
    ) {
      topic.fixedQnCount = 0;
      topic.fixedQnIds = [];
      try {
        console.log("topics", topic);

        setIsLoaded(true);
        if (
          parseInt(libTypeId) === libTypeIds.CODING_CHALLENGE &&
          field === "languages"
        ) {
          debouncedFetchAvailableCount(
            topic.id,
            topic,
            topic.configLibType ?? type ?? libTypeId,
          );
        } else {
          topic.availableQnCount = await fetchAvailableCount(
            topic.id,
            topic,
            topic.configLibType ?? type ?? libTypeId,
          );
        }
      } catch (error) {
        let isUnAuth = error.message.includes("403");
        setShowNotify({
          isUnAuth,
          title: isUnAuth ? "Warning !" : "Error !",
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : "Unable to get test details. Please try again later",
          show: true,
        });
        return;
      } finally {
        setIsLoaded(false);
      }
    }

    // ------------------
    // 3️⃣ Validation
    // ------------------
    const fieldErrors = {};
    const rules = validators[1].topics;
    if (rules[field]) {
      fieldErrors[field] =
        field === "fixedQnCount"
          ? rules[field](value, topic.fixedQnIds)
          : rules[field](value);
    }

    Object.keys(fieldErrors).forEach(
      (k) => !fieldErrors[k] && delete fieldErrors[k],
    );

    // ------------------
    // 5️⃣ Update state
    // ------------------
    setTestDetails(details);

    // ------------------
    // 6️⃣ Update errors
    // ------------------
    setErrors((prev) => {
      const copy = structuredClone(prev ?? {});

      if (!copy[1]) copy[1] = {};
      if (!copy[1].section) copy[1].section = {};
      if (!copy[1].topics) copy[1].topics = {};
      if (!copy[1].section[currentSection.secId])
        copy[1].section[currentSection.secId] = {};

      if (Object.keys(fieldErrors ?? {}).length) {
        copy[1].section[currentSection.secId][id] = fieldErrors;
        copy[1].topics[id] = fieldErrors;
      } else {
        delete copy[1].section?.[currentSection.secId]?.[id];
        delete copy[1].topics?.[id]; // ✅ clear errors when fixed
      }

      return copy;
    });
  };

  const onChangeStudentView = (checked, value) => {
    setTestDetails((prev) => ({ ...prev, viewTest: value }));
    validateField(1, "viewTest", value);
  };

  const steps = useMemo(() => {
    let baseSteps = [
      {
        id: 1,
        label: "Test Details",
        component: (
          <TestForm
            type={libTypeId}
            setType={setLibTypeId}
            testDetails={testDetails}
            setTestDetails={setTestDetails}
            errors={errors[0]}
            setErrors={setErrors}
            onChange={onChangeDetailFields}
            mockuse={mockuse}
            isPublished={isPublished}
            tags={tags}
          />
        ),
      },
      {
        id: 2,
        label: "Test Configuration",
        component: (
          <TestConfig
            type={libTypeId}
            testDetails={testDetails}
            setTestDetails={setTestDetails}
            onChangeName={onChangeSectionName}
            onChangeTopic={onChangeTopicField}
            errors={errors[1]}
            section={currentSection}
            setErrors={setErrors}
            topicErrors={topicErrors}
            mockuse={mockuse}
            isPublished={isPublished}
          />
        ),
      },
      {
        id: 3,
        label: "Student View",
        component: (
          <StudentView
            type={libTypeId}
            onChange={onChangeStudentView}
            value={testDetails.viewTest}
            errors={errors[2]}
            mockuse={mockuse}
            isPublished={isPublished}
          />
        ),
      },
    ];
    if (+testDetails.mode === MODE.PRACTICE_MODE || mockuse === 1) {
      return baseSteps.filter((step) => step.id !== 3);
    }

    return baseSteps;
  }, [
    currentSection,
    libTypeId,
    errors,
    mockuse,
    testDetails,
    topicErrors,
    isPublished,
    tags,
  ]);

  const testName =
    parseInt(type) === libTypeIds.MCQ
      ? "MCQ"
      : parseInt(type) === libTypeIds.CODING_CHALLENGE
        ? "Coding"
        : parseInt(type) === libTypeIds.SQL
          ? "SQL"
          : parseInt(type) === libTypeIds.DESCRIPTIVE && isVideoQnsBool
            ? "Video"
            : parseInt(type) === libTypeIds.DESCRIPTIVE && isAiInterivewBool
              ? "AI"
              : parseInt(type) === libTypeIds.DESCRIPTIVE
                ? "Descriptive"
                : parseInt(type) === libTypeIds.CODING_SNIPPET
                  ? "Coding Snippet"
                  : mockuse === 1
                    ? "Mock"
                    : null;

  if (!testName) return <Error404 />;

  const breadcrumbItems = [
    { label: "Library" },
    {
      label: `${testName} Tests`,
      href:
        mockuse === 1
          ? `/library/mock/test`
          : `/library/${type}/testlist${queryString}`,
    },
    { label: `${isEdit ? "Edit" : "Create"} ${testName} Test` },
  ];

  /* -------- FIELD VALIDATION -------- */

  const validateField = (step, name, value, details = null, type = null) => {
    const validator = validators[step]?.[name];
    if (!validator) return;

    setErrors((prev) => ({
      ...prev,
      [step]: {
        ...prev[step],
        [name]: validator(value, details || testDetails, type || libTypeId),
      },
    }));
  };

  const allowToCheckAvailableQn = (
    libtype,
    topic,
    field,
    isVideoAssessment = false,
  ) => {
    if (parseInt(libtype) === libTypeIds.MCQ || +libtype === libTypeIds.SQL) {
      return (
        (field === "topicId" || field === "complexityId") &&
        topic.topicId &&
        topic.complexityId
      );
    } else if (
      parseInt(libtype) === libTypeIds.DESCRIPTIVE &&
      !isVideoAssessment
    ) {
      return (
        (field === "topicId" ||
          field === "complexityId" ||
          field === "configLibType") &&
        topic.topicId &&
        topic.complexityId &&
        topic.configLibType
      );
    } else if (
      parseInt(libtype) === libTypeIds.DESCRIPTIVE &&
      isVideoAssessment
    ) {
      return (
        (field === "topicId" || field === "complexityId") &&
        topic.topicId &&
        topic.complexityId
      );
    } else if (parseInt(libtype) === libTypeIds.CODING_SNIPPET) {
      return (
        (field === "topicId" || field === "complexityId") &&
        topic.topicId &&
        topic.complexityId
      );
    } else if (parseInt(libtype) === libTypeIds.CODING_CHALLENGE) {
      return (
        (field === "topicId" ||
          field === "complexityId" ||
          field === "languages") &&
        topic.topicId &&
        topic.complexityId &&
        topic.languages?.length
      );
    }
    return false;
  };

  /* -------- ValidateTopic --------*/

  const validateTopic = (topic, isVideoAssessment = false) => {
    const errs = {};

    if (!topic.topicId) errs.topicId = "Select the topic";
    if (!topic.complexityId) errs.complexityId = "Select the difficulty";

    if (
      parseInt(libTypeId) === libTypeIds.DESCRIPTIVE &&
      !isVideoAssessment &&
      !topic.configLibType
    )
      errs.configLibType = "Select the type";
    if (parseInt(libTypeId) === libTypeIds.CODING_CHALLENGE) {
      if (!topic.languages?.length) errs.languages = "Select the language/s";
      if (!Number.isInteger(+topic.duration) || +topic.duration <= 0)
        errs.duration = "Invalid duration";
      if (!topic.durationUnit) errs.durationUnit = "Select the duration unit";
    }

    if (+topic.randomQnCount < 0) errs.randomQnCount = "Invalid number";
    if (+topic.fixedQnCount < 0) errs.fixedQnCount = "Invalid number";

    if (!Number.isInteger(+topic.marks) || +topic.marks <= 0)
      errs.marks = "Invalid mark";

    const r = +topic.randomQnCount || 0;
    const f = +topic.fixedQnCount || 0;

    if (r === 0 && f === 0) {
      errs.qnsCount = "Configure either random or fixed questions";
    }

    if (f > 0 && !topic.fixedQnIds?.length) {
      errs.fixedQnIds = "Please configure questions";
    }

    if (f > 0 && topic.fixedQnIds.length !== f) {
      errs.fixedQnCount =
        topic.fixedQnIds.length > f
          ? "Configured more questions than required"
          : "Configured fewer questions than required";
    }

    if (r + f > topic.availableQnCount) {
      errs.invalidQnCount = "Total questions exceed available questions";
    }

    return errs;
  };

  /* -------- STEP VALIDATION ------- */

  const validateStep = (step) => {
    let stepErrors = {};
    let hasConfigError = false;
    let copyTestDetails = structuredClone(testDetails);

    // ---------------- STEP 0 & 2 ----------------
    if (step === 0 || step === 2) {
      Object.entries(validators[step]).forEach(([field, rule]) => {
        let error = "";

        if (field === "libTypeId") {

          error = rule(libTypeId);

        } else if (field === "scope" && mockuse === 1) {
          error = rule(testDetails[field], testDetails, libTypeId);

        } else if (field === "scope") {

          error = "";

        } else {

          error = rule(testDetails[field]);

        }
        if (error) stepErrors[field] = error;
      });
    }

    // ---------------- STEP 1 ----------------
    if (step === 1) {
      const sectionErrors = {};
      const topicErrors = {};
      let sectionsError = "";

      // reset active section
      copyTestDetails.sections.forEach((s) => (s.isActive = false));

      if (!copyTestDetails.sections.length) {
        sectionsError = "Please add at least one section.";
      }

      for (const sec of copyTestDetails.sections) {
        const secErrs = {};

        if (!sec.secName?.trim()) {
          secErrs.secName = "Section name is required";
        }

        if (!sec.topics?.length) {
          secErrs.topics = "Please add at least one topic";
        }

        for (const topic of sec.topics) {
          const errs = validateTopic(topic, testDetails.videoAssessment);

          if (
            +topic.fixedQnCount + +topic.randomQnCount >
            topic.availableQnCount
          ) {
            errs.invalidQnCount = "Total questions exceed available questions";
            hasConfigError = true;
          }

          if (Object.keys(errs).length) {
            topicErrors[topic.id] = errs;
            secErrs[topic.id] = errs;
          }

          if (hasConfigError) break;
        }

        if (Object.keys(secErrs).length) {
          sectionErrors[sec.secId] = secErrs;
          sec.isActive = true; // focus first invalid section
          break;
        }
      }

      if (sectionsError || Object.keys(sectionErrors).length) {
        stepErrors = {
          sections: sectionsError,
          section: sectionErrors,
          topics: topicErrors,
        };
      }
    }

    // ---------------- TOAST ----------------
    if (Object.keys(stepErrors).length) {
      setToastMessage(
        stepErrors.sections ||
        "Please fix the highlighted errors before proceeding.",
      );
      setToastOpen(true);
    }

    setErrors((prev) => ({ ...prev, [step]: stepErrors }));

    if (hasConfigError) {
      setTestDetails(copyTestDetails);
    }

    return Object.keys(stepErrors).length === 0;
  };

  const onNextOrFinish = async () => {
    let isVaild = validateStep(activeStep);
    if (
      isVaild &&
      (activeStep === 2 ||
        (testDetails.mode === MODE.PRACTICE_MODE && activeStep === 1) ||
        (mockuse === 1 && activeStep !== 0))
    ) {
      try {
        setIsLoaded(true);
        let payload = structuredClone(testDetails);
        console.log("payload for image 1(before) : ", payload);
        let imageFile = null;
        if (payload.image && typeof payload.image !== "string") {
          imageFile = payload.image;
          payload.image = payload.image.name;
        }
        let orderno = 0;
        payload.sections.forEach((sec) =>
          sec.topics.forEach((top) => (top.orderno = ++orderno)),
        );
        payload.libTypeId = parseInt(libTypeId);
        if (mockuse === 1) payload.mockuse = mockuse;
        // /* --- convert duration to low level unit --- */
        // payload.duration =
        //   payload.durationType === "h"
        //     ? parseInt(payload.duration) * 60
        //     : payload.durationType === "d"
        //     ? parseInt(payload.duration) * 24 * 60
        //     : payload.duration;
        if (payload.scope) {
          payload.aiMode = 0;
        }
        if (isAiInterivewBool) {
          payload.aiMode = 1;
        }
        if (isVideoQnsBool) {
          payload.videoAssessment = 1;
        }
        let response;
        if (Number.isInteger(+testid)) {

          response = await axios.post("node/admin/test/update", payload, {
            headers: {
              "Content-Type": "application/json",
            },
          });
        } else {

          response = await axios.post("node/admin/test/create", payload, {
            headers: {
              "Content-Type": "application/json",
            },
          });
          console.log("test creation response : ", response);
        }

        if (
          response?.data?.resultCode === RESULT_STATUS.SUCCESS
          && response?.data?.imageKey
          && imageFile
        ) {

          const fileType =
            imageFile.name.split(".").pop();

          await onUploadS3Object(
            response.data.imageKey,
            imageFile,
            fileType,
            undefined,
            "PUT"
          );
          console.log("Image Upoaded in S3 Successfully");
        }



        if (response?.data.resultCode === RESULT_STATUS.NO_QNS_AVAILABLE) {
          setTopicErrors(response.data.data ?? {});
          setToastMessage(
            "Some topics have insufficient questions. Please fix the errors and try again.",
          );
          setToastOpen(true);
          if (payload.mode === MODE.TEST_MODE)
            setActiveStep((prev) => prev - 1);
        } else if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
          Alert.setShowNotify({
            title: isEdit ? "Test Updated" : "Test Created",
            msg: isEdit
              ? "Test Updated Successfully"
              : "Test Created Successfully",
            show: true,
          });
          if (lpId) {
            if (isEdit) {
              navigate(
                `${baseRoute}/${lpId}/module/${moduleId}?chapterId=${chapterId}`,
              );
              return;
            }
            let tests = [response.data.testId];
            const { data } = await axios.post(
              `node/admin/learningpath/${lpId}/module/${moduleId}/assign-tests`,
              { type: type, tests, chapterId },
            );

            if (data.resultCode === RESULT_STATUS.SUCCESS) {
              navigate(
                `${baseRoute}/${lpId}/module/${moduleId}?chapterId=${chapterId}`,
              );
            } else {
              navigate(`/library/${type}/testlist${queryString}`);
              throw new Error(
                `The test is created but unable to add ${type === libTypeIds.CONTENT
                  ? "content/s"
                  : type === libTypeIds.ASSIGNMENT
                    ? "assignment/s"
                    : "test/s"
                } to path`,
              );
            }
          } else {
            navigate(
              mockuse === 1
                ? `/library/mock/test`
                : `library/${type}/testlist${queryString}`,
            );
          }
        } else {
          setShowNotify({
            title: "Warning !",
            msg:
              response?.data?.msg ??
              `Unable to ${isEdit ? "update" : "create"
              } the test details. Please try again later`,
            show: true,
          });
        }
      } catch (error) {
        console.error("error in save test", error);
        let isUnAuth = error.message.includes("403");
        setShowNotify({
          isUnAuth,
          title: isUnAuth ? "Warning !" : "Error !",
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : error.message ??
            `Unable to ${isEdit ? "update" : "create"
            } the test details. Please try again later`,
          show: true,
        });
      } finally {
        setIsLoaded(false);
      }
    } else return isVaild;
  };

  // console.error("errors", errors[activeStep]);
  const onClickBack = () => {
    if (lpId && moduleId) {
      navigate(
        `${baseRoute}/${lpId}/module/${moduleId}?chapterId=${chapterId}`,
      );
    } else {
      navigate(
        mockuse === 1
          ? `/library/mock/test`
          : `/library/${type}/testlist${queryString}`,
      );
    }
  };

  return (
    <main className="create-test-container">
      <section className="create-test-section-1">
        <ReusableBreadcrumbs items={breadcrumbItems} />
        <div className={`create-test-back-btn-wrapper`} onClick={onClickBack}>
          <svg
            width="7"
            height="12"
            viewBox="0 0 7 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.7998 10.8L0.799805 5.80005L5.7998 0.800049"
              stroke="#667085"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="create-test-back-btn-text">Back</p>
        </div>
        <div className="create-test-title-container">
          <TestSvgIcon libType={libTypeId} />
          <h2 className="create-test-title">
            {isEdit ? "Edit" : "Create"} {testName} Test
          </h2>
        </div>
      </section>
      <section className="create-test-section-2">
        {/* <div className="create-test-section create-test-stepper-section"> */}
        <Stepper
          steps={steps}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          onNext={onNextOrFinish}
          onFinish={onNextOrFinish}
        />
        {/* </div> */}
      </section>
      {isLoaded && <Loading />}
      <Toast
        open={toastOpen}
        message={toastMessage}
        type="warning"
        onClose={() => setToastOpen(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </main>
  );
};

export default CreateTest;
